/**
 * Los hooks de datos: una función por operación de la API.
 *
 * Las vistas no llaman a `api.*` directamente. Pasan por aquí, y por eso aquí
 * —y solo aquí— se declara qué se invalida tras cada mutación. Es la única
 * forma de que registrar una respuesta refresque a la vez el documento, el
 * expediente, el bloque, la cola de Mesa de Partes y el panel.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { api, query } from './cliente.js'
import type { Opcion } from './catalogos.js'
import { claves, RAICES_DEL_MOTOR } from './claves.js'
import type {
  Bloque,
  Cobertura,
  ColaMesaPartes,
  ConfiguracionActa,
  ConfiguracionDocumentos,
  DerivacionOci,
  DerivacionPendiente,
  DiaNoHabil,
  DocumentoDeclarado,
  Entidad,
  EntidadConDisponibilidad,
  EntradaImportacion,
  EstadoMemorandos,
  EstratoSorteo,
  Expediente,
  ExcepcionPermiso,
  ExpedienteResumen,
  FilaRechazada,
  Indicadores,
  ItemSeleccion,
  ItemSeleccionCatalogo,
  LecturaEntidades,
  MapeoColumnas,
  Memorando,
  OficioEnCircuito,
  PadronLeido,
  PaginaAuditoria,
  PrevisualizacionEntidades,
  PasoHistorial,
  PermisoCatalogo,
  ResumenImportacion,
  Politica,
  Requerimiento,
  Rol,
  SeccionConDocumentos,
  SeleccionSorteo,
  Sorteo,
  UsuarioAdmin,
} from './tipos.js'

// ────────────────────────────────────────────────────────────────────────────
// Utilidad compartida
// ────────────────────────────────────────────────────────────────────────────

/**
 * Invalidación tras una mutación del motor.
 *
 * Se invalidan las RAÍCES, no las claves concretas: TanStack Query trata las
 * claves como prefijos, así que invalidar `['documentos']` alcanza a
 * `['documentos', id, 'requerimientos']` sin tener que enumerarlo.
 */
function useInvalidarMotor(): () => Promise<void> {
  const cliente = useQueryClient()
  return async () => {
    await Promise.all(
      RAICES_DEL_MOTOR.map((clave) => cliente.invalidateQueries({ queryKey: clave })),
    )
  }
}

/** Una consulta que solo corre cuando hay id. Evita repetir el `enabled`. */
function consultaOpcional<T>(
  clave: readonly unknown[],
  id: string | undefined,
  traer: (id: string) => Promise<T>,
): { queryKey: readonly unknown[]; queryFn: () => Promise<T>; enabled: boolean } {
  return {
    queryKey: clave,
    queryFn: () => traer(id ?? ''),
    enabled: Boolean(id),
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Panel
// ────────────────────────────────────────────────────────────────────────────

export function usePanel(): UseQueryResult<Indicadores> {
  return useQuery({
    queryKey: claves.panel,
    queryFn: () => api.obtener<Indicadores>('/panel/indicadores'),
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Bloques y sorteo
// ────────────────────────────────────────────────────────────────────────────

export function useBloques(): UseQueryResult<readonly Bloque[]> {
  return useQuery({
    queryKey: claves.bloques.todos,
    queryFn: async () => (await api.obtener<{ bloques: Bloque[] }>('/bloques')).bloques,
  })
}

export function useBloque(id: string | undefined): UseQueryResult<{ bloque: Bloque; sorteo?: Sorteo }> {
  return useQuery(
    consultaOpcional(claves.bloques.uno(id ?? ''), id, (bloqueId) =>
      api.obtener<{ bloque: Bloque; sorteo?: Sorteo }>(`/bloques/${bloqueId}`),
    ),
  )
}

export function useAbrirBloque(): UseMutationResult<{ bloque: Bloque }, Error, string> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (periodo: string) => api.crear<{ bloque: Bloque }>('/bloques', { periodo }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.bloques.todos }),
  })
}

export function useCargarPadron(): UseMutationResult<
  PadronLeido,
  Error,
  { bloqueId: string; archivo: File; hoja?: string }
> {
  return useMutation({
    mutationFn: ({ bloqueId, archivo, hoja }) => {
      const formulario = new FormData()
      formulario.append('archivo', archivo)
      if (hoja) formulario.append('hoja', hoja)
      return api.subir<PadronLeido>(`/bloques/${bloqueId}/padron`, formulario)
    },
  })
}

export interface EntradaSorteo {
  bloqueId: string
  semilla: string
  porcentajeObjetivo: number
  mapeo: MapeoColumnas
  hoja?: string
  /** Además del %: al menos uno de cada distrito fiscal, régimen o convocatoria. */
  coberturas: readonly Cobertura[]
}

export function useEjecutarSorteo(): UseMutationResult<
  { sorteo: Sorteo; estratos: readonly EstratoSorteo[] },
  Error,
  EntradaSorteo
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ bloqueId, ...cuerpo }) =>
      api.crear<{ sorteo: Sorteo; estratos: readonly EstratoSorteo[] }>(
        `/bloques/${bloqueId}/sortear`,
        cuerpo,
      ),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.bloques.todos }),
  })
}

export function useSorteo(id: string | undefined): UseQueryResult<{
  sorteo: Sorteo
  estratos: readonly EstratoSorteo[]
  selecciones: readonly SeleccionSorteo[]
  rechazos: readonly FilaRechazada[]
}> {
  return useQuery(
    consultaOpcional(claves.sorteos.uno(id ?? ''), id, (sorteoId) =>
      api.obtener<{
        sorteo: Sorteo
        estratos: readonly EstratoSorteo[]
        selecciones: readonly SeleccionSorteo[]
        rechazos: readonly FilaRechazada[]
      }>(`/sorteos/${sorteoId}`),
    ),
  )
}

export function useSemillaSugerida(habilitado: boolean): UseQueryResult<{ semilla: string }> {
  return useQuery({
    queryKey: claves.sorteos.semilla,
    queryFn: () => api.obtener<{ semilla: string }>('/sorteos/semilla'),
    enabled: habilitado,
    // Una semilla nueva por sesión de sorteo, no por montaje del componente.
    staleTime: Infinity,
  })
}

/** Lo que responde `confirmarSorteo` en el backend. Leer `expedientes` —que no
 *  existe— era lo que hacía decir al aviso «Se crearon undefined expedientes». */
export interface SorteoConfirmado {
  bloqueId: string
  sorteoId: string
  expedientesCreados: number
  /** El acta oficial se emite en la misma confirmación. */
  numeroActa: string
}

export function useConfirmarSorteo(): UseMutationResult<SorteoConfirmado, Error, string> {
  const invalidar = useInvalidarMotor()
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (sorteoId: string) => api.crear<SorteoConfirmado>(`/sorteos/${sorteoId}/confirmar`),
    onSuccess: async (_datos, sorteoId) => {
      await invalidar()
      // El sorteo que se acaba de ver ya no es el mismo: tiene confirmación y
      // acta. Sin esto, el bloque lo lee de la caché y no muestra el acta.
      await cliente.invalidateQueries({ queryKey: claves.sorteos.uno(sorteoId) })
      // Se emitió un acta: la próxima ya lleva el número siguiente.
      await cliente.invalidateQueries({ queryKey: claves.configuracion.acta })
    },
  })
}

/** El acta firmada digitalmente: queda como evidencia del sorteo, con su huella. */
export function useSubirActaFirmada(): UseMutationResult<
  { sorteo: Sorteo },
  Error,
  { sorteoId: string; archivo: File }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ sorteoId, archivo }) => {
      const formulario = new FormData()
      formulario.append('archivo', archivo)
      return api.subir<{ sorteo: Sorteo }>(`/sorteos/${sorteoId}/acta-firmada`, formulario)
    },
    onSuccess: (_datos, { sorteoId }) =>
      cliente.invalidateQueries({ queryKey: claves.sorteos.uno(sorteoId) }),
  })
}

export function usePoliticaDeBloque(id: string | undefined): UseQueryResult<{
  politica: Politica
  fechaSugerida: string
}> {
  return useQuery(
    consultaOpcional(claves.bloques.politica(id ?? ''), id, (bloqueId) =>
      api.obtener<{ politica: Politica; fechaSugerida: string }>(`/bloques/${bloqueId}/politica`),
    ),
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Expediente
// ────────────────────────────────────────────────────────────────────────────

export function useExpedientesDeBloque(
  bloqueId: string | undefined,
): UseQueryResult<readonly ExpedienteResumen[]> {
  return useQuery(
    consultaOpcional(claves.bloques.expedientes(bloqueId ?? ''), bloqueId, async (id) => {
      const datos = await api.obtener<{ expedientes: ExpedienteResumen[] }>(`/bloques/${id}/expedientes`)
      return datos.expedientes
    }),
  )
}

export function useExpediente(id: string | undefined): UseQueryResult<{
  expediente: Expediente
  seleccion: readonly ItemSeleccion[]
  secciones: readonly SeccionConDocumentos[]
}> {
  return useQuery(
    consultaOpcional(claves.expedientes.uno(id ?? ''), id, (expedienteId) =>
      api.obtener<{
        expediente: Expediente
        seleccion: readonly ItemSeleccion[]
        secciones: readonly SeccionConDocumentos[]
      }>(`/expedientes/${expedienteId}`),
    ),
  )
}

export function useVerificarItem(): UseMutationResult<
  { verificado: true },
  Error,
  { itemId: string; observacion?: string; archivo?: File }
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ itemId, observacion, archivo }) => {
      const formulario = new FormData()
      if (observacion) formulario.append('observacion', observacion)
      if (archivo) formulario.append('archivo', archivo)
      return api.subir<{ verificado: true }>(`/items-seleccion/${itemId}/verificar`, formulario)
    },
    onSuccess: invalidar,
  })
}

export interface EntradaDocumento {
  expedienteId: string
  seccion: string
  denominacion: string
  entidadId?: string
  entidadTexto?: string
  numeroDocumento?: string
  fechaEmisionDocumento?: string
  archivo?: File
}

export function useRegistrarDocumento(): UseMutationResult<
  { documentoDeclaradoId: string },
  Error,
  EntradaDocumento
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ expedienteId, archivo, ...campos }) => {
      const formulario = new FormData()
      for (const [clave, valor] of Object.entries(campos)) {
        if (valor) formulario.append(clave, String(valor))
      }
      if (archivo) formulario.append('archivo', archivo)
      return api.subir<{ documentoDeclaradoId: string }>(
        `/expedientes/${expedienteId}/documentos-declarados`,
        formulario,
      )
    },
    onSuccess: invalidar,
  })
}

export function useDocumento(id: string | undefined): UseQueryResult<DocumentoDeclarado> {
  return useQuery(
    consultaOpcional(claves.documentos.uno(id ?? ''), id, async (documentoId) => {
      const datos = await api.obtener<{ documento: DocumentoDeclarado }>(
        `/documentos-declarados/${documentoId}`,
      )
      return datos.documento
    }),
  )
}

export function useDescartarDocumento(): UseMutationResult<
  { descartado: true },
  Error,
  { documentoId: string; motivo: string }
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ documentoId, motivo }) =>
      api.crear<{ descartado: true }>(`/documentos-declarados/${documentoId}/descartar`, { motivo }),
    onSuccess: invalidar,
  })
}

// ────────────────────────────────────────────────────────────────────────────
// El motor: requerimientos, pasos y respuestas
// ────────────────────────────────────────────────────────────────────────────

export interface EntradaEmision {
  documentoId: string
  canales: ReadonlyArray<{ canal: string; canalEntidadId?: string }>
  fechaEmision?: string
  motivo?: string
  /** A quién se dirige el oficio: se propone desde el catálogo y se puede corregir. */
  destinatario?: { nombre: string; cargo: string }
}

export function useEmitirRequerimiento(): UseMutationResult<
  { requerimiento: Requerimiento; consultasAbiertas: number },
  Error,
  EntradaEmision
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ documentoId, ...cuerpo }) =>
      api.crear<{ requerimiento: Requerimiento; consultasAbiertas: number }>(
        `/documentos-declarados/${documentoId}/requerimientos`,
        cuerpo,
      ),
    onSuccess: invalidar,
  })
}

/** Lo que vuelve firmado del trámite documentario: su número, su expediente y el PDF. */
export interface EntradaFirmado {
  numero: string
  expediente?: string
  archivo: File
}

function formularioFirmado({ numero, expediente, archivo }: EntradaFirmado): FormData {
  const formulario = new FormData()
  formulario.append('numero', numero)
  if (expediente) formulario.append('expediente', expediente)
  formulario.append('archivo', archivo)
  return formulario
}

export function useRegistrarOficioFirmado(): UseMutationResult<
  { requerimiento: Requerimiento },
  Error,
  EntradaFirmado & { requerimientoId: string }
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ requerimientoId, ...firmado }) =>
      api.subir<{ requerimiento: Requerimiento }>(
        `/requerimientos/${requerimientoId}/oficio-firmado`,
        formularioFirmado(firmado),
      ),
    onSuccess: invalidar,
  })
}

export function useRequerimientosDeDocumento(
  documentoId: string | undefined,
): UseQueryResult<readonly Requerimiento[]> {
  return useQuery(
    consultaOpcional(claves.documentos.requerimientos(documentoId ?? ''), documentoId, async (id) => {
      const datos = await api.obtener<{ requerimientos: Requerimiento[] }>(
        `/documentos-declarados/${id}/requerimientos`,
      )
      return datos.requerimientos
    }),
  )
}

export interface EntradaPaso {
  consultaId: string
  paso: number
  fecha: string
  observacion?: string
  numeroCargo?: string
  archivo?: File
}

export function useRegistrarPaso(): UseMutationResult<
  { requerimiento: Requerimiento },
  Error,
  EntradaPaso
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ consultaId, archivo, paso, ...campos }) => {
      const formulario = new FormData()
      formulario.append('paso', String(paso))
      for (const [clave, valor] of Object.entries(campos)) {
        if (valor) formulario.append(clave, String(valor))
      }
      if (archivo) formulario.append('archivo', archivo)
      return api.subir<{ requerimiento: Requerimiento }>(`/consultas/${consultaId}/pasos`, formulario)
    },
    onSuccess: invalidar,
  })
}

export interface EntradaRespuesta {
  consultaId: string
  fechaRecepcion: string
  pronunciamiento: string
  referencia?: string
  observacion: string
  /** `NEG-3`: obligatoria, sin excepción. El tipo lo refleja: no es opcional. */
  archivo: File
}

export interface ResultadoRespuesta {
  requerimiento: Requerimiento
  /** `NEG-8`: si fue concluyente, cerró el requerimiento. */
  cerroRequerimiento: boolean
  consultasAbandonadas: number
  /** El cierre formal se registró junto con la respuesta. */
  pasoCierreRegistrado: boolean
}

export function useRegistrarRespuesta(): UseMutationResult<
  ResultadoRespuesta,
  Error,
  EntradaRespuesta
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ consultaId, archivo, ...campos }) => {
      const formulario = new FormData()
      for (const [clave, valor] of Object.entries(campos)) {
        if (valor) formulario.append(clave, String(valor))
      }
      formulario.append('archivo', archivo)
      return api.subir<ResultadoRespuesta>(`/consultas/${consultaId}/respuesta`, formulario)
    },
    onSuccess: invalidar,
  })
}

export function useAnularRequerimiento(): UseMutationResult<
  { anulado: true; consumeOrdinal: false },
  Error,
  { requerimientoId: string; motivo: string }
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ requerimientoId, motivo }) =>
      api.crear<{ anulado: true; consumeOrdinal: false }>(
        `/requerimientos/${requerimientoId}/anular`,
        { motivo },
      ),
    onSuccess: invalidar,
  })
}

export function useAnularConsulta(): UseMutationResult<
  { anulada: true },
  Error,
  { consultaId: string; motivo: string }
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ consultaId, motivo }) =>
      api.crear<{ anulada: true }>(`/consultas/${consultaId}/anular`, { motivo }),
    onSuccess: invalidar,
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Entidades
// ────────────────────────────────────────────────────────────────────────────

export function useEntidades(incluirDesactivadas = false): UseQueryResult<
  readonly EntidadConDisponibilidad[]
> {
  return useQuery({
    queryKey: claves.entidades.lista(incluirDesactivadas),
    queryFn: async () => {
      const datos = await api.obtener<{ entidades: EntidadConDisponibilidad[] }>(
        `/entidades${query({ incluirDesactivadas: incluirDesactivadas || undefined })}`,
      )
      return datos.entidades
    },
  })
}

export interface EntradaEntidad {
  nombre: string
  siglas?: string
  tipo: string
  ruc?: string
  /** A quién se dirige el oficio. Vacío lo borra al editar. */
  representanteNombre?: string
  representanteCargo?: string
}

export function useCrearEntidad(): UseMutationResult<{ entidad: Entidad }, Error, EntradaEntidad> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo) => api.crear<{ entidad: Entidad }>('/entidades', cuerpo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.entidades.todas }),
  })
}

/**
 * Alta de un tipo de entidad, desde el modal que crea la entidad.
 *
 * Invalida **los catálogos**, no las entidades: el tipo nuevo tiene que
 * aparecer en el desplegable, y ese desplegable se sirve de `/api/catalogos`.
 * Es el único campo del catálogo que deja de ser constante del despliegue.
 */
export function useCrearTipoEntidad(): UseMutationResult<Opcion, Error, string> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (etiqueta) => {
      const datos = await api.crear<{ tipo: Opcion }>('/entidades/tipos', { etiqueta })
      return datos.tipo
    },
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.catalogos }),
  })
}

// ── Importación masiva de entidades ─────────────────────────────────────────
// Tres pasos, como el padrón: subir, ver qué saldría, confirmar. El archivo
// queda en el servidor entre uno y otro, así que aquí solo viaja el mapeo.

export function useCargarListadoEntidades(): UseMutationResult<
  LecturaEntidades,
  Error,
  { archivo: File; hoja?: string }
> {
  return useMutation({
    mutationFn: ({ archivo, hoja }) => {
      const formulario = new FormData()
      formulario.append('archivo', archivo)
      if (hoja) formulario.append('hoja', hoja)
      return api.subir<LecturaEntidades>('/entidades/importacion', formulario)
    },
  })
}

export function usePrevisualizarEntidades(): UseMutationResult<
  PrevisualizacionEntidades,
  Error,
  EntradaImportacion
> {
  return useMutation({
    mutationFn: (cuerpo) =>
      api.crear<PrevisualizacionEntidades>('/entidades/importacion/previsualizar', cuerpo),
  })
}

export function useConfirmarImportacionEntidades(): UseMutationResult<
  ResumenImportacion,
  Error,
  EntradaImportacion
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo) =>
      api.crear<ResumenImportacion>('/entidades/importacion/confirmar', cuerpo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.entidades.todas })
      // También los catálogos: la importación pudo crear tipos nuevos.
      void cliente.invalidateQueries({ queryKey: claves.catalogos })
    },
  })
}

export function useActualizarEntidad(): UseMutationResult<
  { entidad: Entidad },
  Error,
  { id: string; cambios: Partial<EntradaEntidad> & { desactivar?: boolean } }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, cambios }) => api.modificar<{ entidad: Entidad }>(`/entidades/${id}`, cambios),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.entidades.todas }),
  })
}

export interface EntradaCanal {
  canal: string
  destino: string
  etiqueta?: string
  esOficial: boolean
  plazoDias?: number
}

export function useAgregarCanal(): UseMutationResult<
  { entidad: Entidad },
  Error,
  { entidadId: string; canal: EntradaCanal }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ entidadId, canal }) =>
      api.crear<{ entidad: Entidad }>(`/entidades/${entidadId}/canales`, canal),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.entidades.todas }),
  })
}

export function useActualizarCanal(): UseMutationResult<
  { actualizado: true; consultasAbiertas: number },
  Error,
  { canalId: string; cambios: Partial<EntradaCanal> & { desactivar?: boolean } }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ canalId, cambios }) =>
      api.modificar<{ actualizado: true; consultasAbiertas: number }>(
        `/canales-entidad/${canalId}`,
        cambios,
      ),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.entidades.todas }),
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Circuito
// ────────────────────────────────────────────────────────────────────────────

export function useColaMesaPartes(): UseQueryResult<ColaMesaPartes> {
  return useQuery({
    queryKey: claves.circuito.colaMesaPartes,
    queryFn: () => api.obtener<ColaMesaPartes>('/circuito/cola-mesa-partes'),
  })
}

export function useHistorialMesaPartes(): UseQueryResult<readonly PasoHistorial[]> {
  return useQuery({
    queryKey: claves.circuito.historialMesaPartes,
    queryFn: async () =>
      (await api.obtener<{ pasos: PasoHistorial[] }>('/circuito/historial-mesa-partes')).pasos,
  })
}

export function useOficiosAsignados(): UseQueryResult<readonly OficioEnCircuito[]> {
  return useQuery({
    queryKey: claves.circuito.asignadas,
    queryFn: async () =>
      (await api.obtener<{ oficios: OficioEnCircuito[] }>('/circuito/asignadas')).oficios,
  })
}

export function useNotificadores(): UseQueryResult<ReadonlyArray<{ id: string; nombre: string }>> {
  return useQuery({
    queryKey: claves.circuito.notificadores,
    queryFn: async () =>
      (await api.obtener<{ notificadores: Array<{ id: string; nombre: string }> }>(
        '/circuito/notificadores',
      )).notificadores,
  })
}

export function useAsignarNotificador(): UseMutationResult<
  { asignada: true },
  Error,
  { consultaId: string; notificadorId: string }
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ consultaId, notificadorId }) =>
      api.crear<{ asignada: true }>(`/consultas/${consultaId}/asignar`, { notificadorId }),
    onSuccess: invalidar,
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Control — OCI
// ────────────────────────────────────────────────────────────────────────────

export function useDerivaciones(causa?: string): UseQueryResult<readonly DerivacionOci[]> {
  return useQuery({
    queryKey: claves.oci.lista(causa),
    queryFn: async () =>
      (await api.obtener<{ derivaciones: DerivacionOci[] }>(`/derivaciones-oci${query({ causa })}`))
        .derivaciones,
  })
}

export function useDerivacionesPendientes(): UseQueryResult<readonly DerivacionPendiente[]> {
  return useQuery({
    queryKey: claves.oci.pendientes,
    queryFn: async () =>
      (await api.obtener<{ pendientes: DerivacionPendiente[] }>('/derivaciones-oci/pendientes'))
        .pendientes,
  })
}

export function useDerivarAlOci(): UseMutationResult<
  { derivacionId: string; numeroInforme: string; causa: string },
  Error,
  { documentoId: string; causa: string; motivo: string }
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ documentoId, causa, motivo }) =>
      api.crear<{ derivacionId: string; numeroInforme: string; causa: string }>(
        `/documentos-declarados/${documentoId}/derivar-oci`,
        { causa, motivo },
      ),
    onSuccess: invalidar,
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Configuración
// ────────────────────────────────────────────────────────────────────────────

export function usePolitica(): UseQueryResult<Politica> {
  return useQuery({
    queryKey: claves.configuracion.politica,
    queryFn: async () =>
      (await api.obtener<{ politica: Politica }>('/configuracion/politica')).politica,
  })
}

export function useGuardarPolitica(): UseMutationResult<
  { politica: Politica; consultasConPlazoEnCurso: number },
  Error,
  Omit<Politica, 'vigenteDesde'> & { motivo: string }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo) =>
      api.reemplazar<{ politica: Politica; consultasConPlazoEnCurso: number }>(
        '/configuracion/politica',
        cuerpo,
      ),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.configuracion.politica }),
  })
}

export interface EstadoConfiguracionActa {
  configuracion: ConfiguracionActa
  /** El número que llevaría la próxima acta de este año. */
  proximoNumero: string
}

export function useConfiguracionActa(): UseQueryResult<EstadoConfiguracionActa> {
  return useQuery({
    queryKey: claves.configuracion.acta,
    queryFn: () => api.obtener<EstadoConfiguracionActa>('/configuracion/acta'),
  })
}

export function useGuardarConfiguracionActa(): UseMutationResult<
  EstadoConfiguracionActa,
  Error,
  ConfiguracionActa
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo) => api.reemplazar<EstadoConfiguracionActa>('/configuracion/acta', cuerpo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.configuracion.acta }),
  })
}

export function useConfiguracionDocumentos(): UseQueryResult<ConfiguracionDocumentos> {
  return useQuery({
    queryKey: claves.configuracion.documentos,
    queryFn: async () => {
      const datos = await api.obtener<{ configuracion: ConfiguracionDocumentos }>('/configuracion/documentos')
      return datos.configuracion
    },
  })
}

export function useGuardarConfiguracionDocumentos(): UseMutationResult<
  { configuracion: ConfiguracionDocumentos },
  Error,
  ConfiguracionDocumentos
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo) =>
      api.reemplazar<{ configuracion: ConfiguracionDocumentos }>('/configuracion/documentos', cuerpo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.configuracion.documentos }),
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Culminar y memorandos
// ────────────────────────────────────────────────────────────────────────────

export function useMemorandos(expedienteId: string | undefined): UseQueryResult<EstadoMemorandos> {
  return useQuery(
    consultaOpcional(claves.expedientes.memorandos(expedienteId ?? ''), expedienteId, (id) =>
      api.obtener<EstadoMemorandos>(`/expedientes/${id}/memorandos`),
    ),
  )
}

/** Culminar: se congela el memorando de resultados, aunque queden respuestas en espera. */
export function useCulminarExpediente(): UseMutationResult<{ memorando: Memorando }, Error, string> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: (expedienteId) => api.crear<{ memorando: Memorando }>(`/expedientes/${expedienteId}/culminar`),
    onSuccess: invalidar,
  })
}

export function useGenerarComplementario(): UseMutationResult<{ memorando: Memorando }, Error, string> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: (documentoId) =>
      api.crear<{ memorando: Memorando }>(`/documentos-declarados/${documentoId}/memorando-complementario`),
    onSuccess: invalidar,
  })
}

export function useRegistrarMemorandoFirmado(): UseMutationResult<
  { memorando: Memorando },
  Error,
  EntradaFirmado & { memorandoId: string }
> {
  const invalidar = useInvalidarMotor()
  return useMutation({
    mutationFn: ({ memorandoId, ...firmado }) =>
      api.subir<{ memorando: Memorando }>(`/memorandos/${memorandoId}/firmado`, formularioFirmado(firmado)),
    onSuccess: invalidar,
  })
}

export function useCalendario(): UseQueryResult<readonly DiaNoHabil[]> {
  return useQuery({
    queryKey: claves.configuracion.calendario,
    queryFn: async () => (await api.obtener<{ dias: DiaNoHabil[] }>('/calendario')).dias,
  })
}

export function useGuardarCalendario(): UseMutationResult<
  { dias: readonly DiaNoHabil[]; consultasConPlazoEnCurso: number },
  Error,
  readonly DiaNoHabil[]
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dias) =>
      api.reemplazar<{ dias: readonly DiaNoHabil[]; consultasConPlazoEnCurso: number }>(
        '/calendario',
        { dias },
      ),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.configuracion.calendario }),
  })
}

export function useItemsSeleccion(): UseQueryResult<readonly ItemSeleccionCatalogo[]> {
  return useQuery({
    queryKey: claves.configuracion.itemsSeleccion,
    queryFn: async () =>
      (await api.obtener<{ items: ItemSeleccionCatalogo[] }>('/configuracion/items-seleccion')).items,
  })
}

export function useGuardarItemsSeleccion(): UseMutationResult<
  { items: readonly ItemSeleccionCatalogo[]; aplicaAExpedientesNuevos: true },
  Error,
  readonly ItemSeleccionCatalogo[]
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (items) =>
      api.reemplazar<{ items: readonly ItemSeleccionCatalogo[]; aplicaAExpedientesNuevos: true }>(
        '/configuracion/items-seleccion',
        { items },
      ),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.configuracion.itemsSeleccion }),
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Administración
// ────────────────────────────────────────────────────────────────────────────

export interface RolesYMatriz {
  roles: readonly Rol[]
  matriz: Readonly<Record<string, readonly string[]>>
  permisos: readonly PermisoCatalogo[]
}

export function useRoles(): UseQueryResult<RolesYMatriz> {
  return useQuery({
    queryKey: claves.administracion.roles,
    queryFn: () => api.obtener<RolesYMatriz>('/roles'),
  })
}

export function useCrearRol(): UseMutationResult<
  { roles: readonly Rol[] },
  Error,
  { codigo: string; nombre: string; descripcion: string }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo) => api.crear<{ roles: readonly Rol[] }>('/roles', cuerpo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.administracion.roles }),
  })
}

export function useActualizarRol(): UseMutationResult<
  { roles: readonly Rol[] },
  Error,
  { codigo: string; cambios: { nombre?: string; descripcion?: string; activo?: boolean } }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ codigo, cambios }) =>
      api.modificar<{ roles: readonly Rol[] }>(`/roles/${codigo}`, cambios),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.administracion.roles }),
  })
}

export function useGuardarPermisosDeRol(): UseMutationResult<
  { matriz: Readonly<Record<string, readonly string[]>>; cuentasAfectadas: number },
  Error,
  { codigo: string; permisos: readonly string[] }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ codigo, permisos }) =>
      api.reemplazar<{
        matriz: Readonly<Record<string, readonly string[]>>
        cuentasAfectadas: number
      }>(`/roles/${codigo}/permisos`, { permisos }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: claves.administracion.roles })
      // Los permisos del propio actor pueden haber cambiado: si no se refresca
      // la sesión, el menú lateral sigue mostrando lo que ya no puede usar.
      await cliente.invalidateQueries({ queryKey: claves.sesion })
    },
  })
}

export function useUsuarios(): UseQueryResult<readonly UsuarioAdmin[]> {
  return useQuery({
    queryKey: claves.administracion.usuarios,
    queryFn: async () => (await api.obtener<{ usuarios: UsuarioAdmin[] }>('/usuarios')).usuarios,
  })
}

export function useCrearUsuario(): UseMutationResult<
  { usuario: UsuarioAdmin; passwordTemporal: string },
  Error,
  { nombre: string; correo: string; rol: string }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo) =>
      api.crear<{ usuario: UsuarioAdmin; passwordTemporal: string }>('/usuarios', cuerpo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.administracion.usuarios }),
  })
}

export function useActualizarUsuario(): UseMutationResult<
  { actualizado: true },
  Error,
  { id: string; cambios: { nombre?: string; rol?: string; activo?: boolean } }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, cambios }) => api.modificar<{ actualizado: true }>(`/usuarios/${id}`, cambios),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.administracion.usuarios }),
  })
}

export function useRestablecerPassword(): UseMutationResult<
  { passwordTemporal: string; sesionesCerradas: true },
  Error,
  { id: string; motivo: string }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, motivo }) =>
      api.crear<{ passwordTemporal: string; sesionesCerradas: true }>(
        `/usuarios/${id}/password-reset`,
        { motivo },
      ),
    onSuccess: () => cliente.invalidateQueries({ queryKey: claves.administracion.usuarios }),
  })
}

export function useExcepciones(usuarioId: string | undefined): UseQueryResult<
  readonly ExcepcionPermiso[]
> {
  return useQuery(
    consultaOpcional(claves.administracion.excepciones(usuarioId ?? ''), usuarioId, async (id) => {
      const datos = await api.obtener<{ excepciones: ExcepcionPermiso[] }>(`/usuarios/${id}/permisos`)
      return datos.excepciones
    }),
  )
}

export function useOtorgarExcepcion(): UseMutationResult<
  { excepciones: readonly ExcepcionPermiso[] },
  Error,
  {
    usuarioId: string
    permiso: string
    otorgado: boolean
    vigenteDesde: string
    vigenteHasta?: string
    motivo: string
  }
> {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ usuarioId, ...cuerpo }) =>
      api.crear<{ excepciones: readonly ExcepcionPermiso[] }>(`/usuarios/${usuarioId}/permisos`, cuerpo),
    onSuccess: async (_datos, variables) => {
      await cliente.invalidateQueries({
        queryKey: claves.administracion.excepciones(variables.usuarioId),
      })
      await cliente.invalidateQueries({ queryKey: claves.sesion })
    },
  })
}

export interface FiltrosAuditoria {
  entidad?: string
  usuarioId?: string
  desde?: string
  hasta?: string
  pagina: number
  tamano: number
}

export function useAuditoria(filtros: FiltrosAuditoria): UseQueryResult<PaginaAuditoria> {
  return useQuery({
    queryKey: claves.auditoria(filtros),
    queryFn: () => api.obtener<PaginaAuditoria>(`/auditoria${query({ ...filtros })}`),
  })
}
